import React from 'react';
import PidDiagramShell, { PidPipe, PidPort } from './PidDiagramShell';
import { OlefinRoute } from '../../types';

/** 生物质制烯烃（BTO）P&ID 详细图：生物质气化 → 净化 → 甲醇合成 → MTO → 分离 */
const media = {
  bio: { label: '生物质', light: '#558b2f', dark: '#aed581', width: 2.6 },
  syngas: { label: '合成气 / 净化气', light: '#5c6bc0', dark: '#9fa8da', width: 2.6 },
  meth: { label: '甲醇', light: '#00838f', dark: '#4dd0e1', width: 2.6 },
  gas: { label: '反应油气 / 烯烃', light: '#2e7d32', dark: '#81c784', width: 2.6 },
  cat: { label: '催化剂（循环输送）', light: '#8e24aa', dark: '#ce93d8', width: 3.2, dash: '12 5' },
  water: { label: '水', light: '#1565c0', dark: '#64b5f6', width: 2 },
};

const devices = [
  { id: 'r101', kind: 'reactor' as const, x: 140, y: 128, tag: 'R-101', name: '生物质气化炉' },
  { id: 't101', kind: 'column' as const, x: 320, y: 94, tag: 'T-101', name: '合成气净化', note: '水洗脱杂' },
  { id: 'r102', kind: 'reactor' as const, x: 490, y: 128, tag: 'R-102', name: '甲醇合成塔' },
  { id: 'v101', kind: 'vDrum' as const, x: 660, y: 161, tag: 'V-101', name: '甲醇分离器' },
  { id: 'r103', kind: 'reactor' as const, x: 820, y: 128, tag: 'R-103', name: 'MTO反应器', note: '流化床' },
  { id: 'd101', kind: 'vDrum' as const, x: 990, y: 161, tag: 'D-101', name: '沉降器' },
  { id: 'rg101', kind: 'regenerator' as const, x: 763, y: 430, tag: 'RG-101', name: '再生器' },
  { id: 't102', kind: 'column' as const, x: 1160, y: 101, tag: 'T-102', name: '急冷塔' },
  { id: 't103', kind: 'column' as const, x: 1310, y: 101, tag: 'T-103', name: '乙烯精馏塔' },
  { id: 't104', kind: 'column' as const, x: 1450, y: 101, tag: 'T-104', name: '丙烯精馏塔' },
];

const ports: PidPort[] = [
  { id: 'p-bio', label: '生物质', medium: 'bio', x: 0, y: 224, direction: 'in' as const },
  { id: 'p-air', label: '气化风', medium: 'water', x: 60, y: 330, direction: 'in' as const },
  { id: 'p-ethylene', label: '乙烯', medium: 'gas', x: 1610, y: 611, direction: 'out' as const },
  { id: 'p-propylene', label: '丙烯', medium: 'meth', x: 1610, y: 681, direction: 'out' as const },
];

const pipes: PidPipe[] = [
  { id: 's01', from: 'p-bio', fromSide: 'right', to: 'r101', toSide: 'left', medium: 'bio' },
  { id: 's02', from: 'p-air', fromSide: 'right', to: 'r101', toSide: 'bottom', medium: 'water' },
  { id: 's03', from: 'r101', fromSide: 'right', to: 't101', toSide: 'left', medium: 'syngas' },
  { id: 's04', from: 't101', fromSide: 'right', to: 'r102', toSide: 'left', medium: 'syngas' },
  { id: 's05', from: 'r102', fromSide: 'right', to: 'v101', toSide: 'left', medium: 'meth' },
  { id: 's06', from: 'v101', fromSide: 'right', to: 'r103', toSide: 'left', medium: 'meth' },
  { id: 's07', from: 'r103', fromSide: 'right', to: 'd101', toSide: 'left', medium: 'gas' },
  { id: 's08', from: 'd101', fromSide: 'top', to: 't102', toSide: 'left', medium: 'gas' },
  { id: 's09', from: 't102', fromSide: 'right', to: 't103', toSide: 'left', medium: 'gas' },
  { id: 's10', from: 't103', fromSide: 'right', to: 't104', toSide: 'left', medium: 'gas' },
  { id: 's11', from: 't103', fromSide: 'bottom', to: 'p-ethylene', toSide: 'left', medium: 'gas' },
  { id: 's12', from: 't104', fromSide: 'bottom', to: 'p-propylene', toSide: 'left', medium: 'meth' },
  { id: 's13', from: 'd101', fromSide: 'bottom', to: 'rg101', toSide: 'right', medium: 'cat' },
  { id: 's14', from: 'rg101', fromSide: 'top', to: 'r103', toSide: 'bottom', medium: 'cat' },
];

const BtoDiagram: React.FC<{ route: OlefinRoute }> = ({ route }) => (
  <PidDiagramShell
    routeName={route.name}
    title="BTO 装置详细流程"
    subtitle="生物质气化 → 合成气净化 → 甲醇合成/分离 → MTO 流化床反应器（催化剂循环再生）→ 急冷 → 乙烯/丙烯精馏，示范阶段工艺（设备形状按 P&ID 惯例，管线按介质分色）"
    media={media}
    devices={devices}
    ports={ports}
    pipes={pipes}
    feedstock={route.feedstock}
    products={[...route.products, ...(route.byproducts ?? [])]}
  />
);

export default BtoDiagram;
